import { useToast, workdaySyncService } from '@aslanpay/portal-common-lib';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { DeductionsReportPage } from 'app/components/UI/Table/Table.types';

interface ResubmitDeductionsReportParams {
  deductionsReportId: string;
}

interface ResubmitDeductionsReportContext {
  hasCachedReport: boolean;
  previousSubmittedAt?: string;
}

const MAX_SUBMISSION_STATUS_REFRESH_ATTEMPTS = 5;
const SUBMISSION_STATUS_REFRESH_DELAY_MS = 1000;
const DEDUCTIONS_REPORTS_QUERY_KEY = ['fetchDeductionsReportsPage'];

const delay = (duration: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, duration);
  });

const useResubmitDeductionsReport = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const getCachedReport = (deductionsReportId: string) =>
    queryClient
      .getQueriesData<DeductionsReportPage>({ queryKey: DEDUCTIONS_REPORTS_QUERY_KEY })
      .flatMap(([, page]) => page?.list ?? [])
      .find((report) => report.id === deductionsReportId);

  const refetchUntilSubmissionStatusChanges = async (
    deductionsReportId: string,
    previousSubmittedAt: string | undefined,
    attempt = 0
  ): Promise<void> => {
    await queryClient.refetchQueries({ queryKey: DEDUCTIONS_REPORTS_QUERY_KEY });
    const report = getCachedReport(deductionsReportId);
    const hasNewSubmission = report?.payrollIntegrationSubmittedAt !== previousSubmittedAt;
    const hasSubmissionStatus = report?.payrollIntegrationSubmissionStatus !== undefined;

    if (
      (hasNewSubmission && hasSubmissionStatus) ||
      attempt === MAX_SUBMISSION_STATUS_REFRESH_ATTEMPTS - 1
    ) {
      return;
    }

    await delay(SUBMISSION_STATUS_REFRESH_DELAY_MS);
    await refetchUntilSubmissionStatusChanges(deductionsReportId, previousSubmittedAt, attempt + 1);
  };

  return useMutation({
    mutationFn: ({ deductionsReportId }: ResubmitDeductionsReportParams) =>
      workdaySyncService.resubmitDeductionsReport(deductionsReportId),
    onMutate: ({ deductionsReportId }): ResubmitDeductionsReportContext => {
      const report = getCachedReport(deductionsReportId);

      return {
        hasCachedReport: report !== undefined,
        previousSubmittedAt: report?.payrollIntegrationSubmittedAt,
      };
    },
    onSuccess: (result) => {
      const description =
        result?.integrationFound && result.totalRows !== undefined && result.submitted !== undefined
          ? `Submitted ${result.submitted} / ${result.totalRows} rows.`
          : 'The report has been resubmitted.';

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Resubmission started',
        description,
      });
    },
    onSettled: (_, __, { deductionsReportId }, context) => {
      const refreshSubmissionStatus = context?.hasCachedReport
        ? refetchUntilSubmissionStatusChanges(deductionsReportId, context.previousSubmittedAt)
        : queryClient.refetchQueries({ queryKey: DEDUCTIONS_REPORTS_QUERY_KEY });

      refreshSubmissionStatus.catch(() => undefined);
    },
  });
};

export default useResubmitDeductionsReport;
